import type { ChatStreamEvent, ChatStreamEventType } from '@/types/agent-chat';

const CHAT_STREAM_EVENT_TYPES = [
  'message_start',
  'progress',
  'delta',
  'tool_start',
  'tool_result',
  'task_created',
  'structured_content',
  'final',
  'error',
  'done',
  'ping',
] as const satisfies readonly ChatStreamEventType[];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const getString = (
  source: Record<string, unknown>,
  key: string
): string | undefined => {
  const value = source[key];
  return typeof value === 'string' ? value : undefined;
};

export const isChatStreamEventType = (
  value: unknown
): value is ChatStreamEventType =>
  typeof value === 'string' &&
  CHAT_STREAM_EVENT_TYPES.includes(value as ChatStreamEventType);

export const parseChatStreamEventData = (
  data: string,
  fallbackType?: string
): ChatStreamEvent | null => {
  if (!data || data === '[DONE]') return null;

  const parsed: unknown = JSON.parse(data);
  if (!isRecord(parsed)) {
    throw new Error('Chat stream event payload must be a JSON object.');
  }

  const parsedType = parsed.type;
  const type = isChatStreamEventType(parsedType)
    ? parsedType
    : isChatStreamEventType(fallbackType)
      ? fallbackType
      : undefined;

  if (!type) {
    throw new Error('Chat stream event payload is missing a known type.');
  }

  const metadata = parsed.metadata;

  return {
    eventId: getString(parsed, 'eventId'),
    type,
    conversationId: getString(parsed, 'conversationId'),
    messageId: getString(parsed, 'messageId'),
    agentKey: getString(parsed, 'agentKey'),
    text: getString(parsed, 'text'),
    status: getString(parsed, 'status'),
    taskId: getString(parsed, 'taskId'),
    toolName: getString(parsed, 'toolName'),
    label: getString(parsed, 'label'),
    metadata: isRecord(metadata) ? metadata : undefined,
    data: parsed.data,
    createdAt: getString(parsed, 'createdAt'),
  };
};
